import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Banner, Button } from '@immo/ui-kit';
import { FLEET_ASSISTANT_TEXT_MAX_CHARS } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon } from '@/components/layout/icons';
import type { SendResult } from '@/components/fleet/board/use-assistant-conversation';

type Props = {
  readonly onSend: (text: string) => Promise<SendResult>;
};

export function AssistantComposer({ onSend }: Props) {
  const t = useTranslations('pages.admin.fleet-board.assistant');
  const tErrors = useTranslations('errors');
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const trimmed = value.trim();
  const canSend = trimmed !== '' && !sending;

  const submit = async () => {
    if (!canSend) return;

    setSending(true);

    try {
      const result = await onSend(trimmed);

      if (result.ok) {
        setValue('');
        setErrorCode(null);
      } else {
        setErrorCode(result.code ?? 'generic.unexpected');
      }
    } catch {
      setErrorCode('generic.unexpected');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-2">
      {errorCode !== null && <Banner variant="danger" icon={<AlertCircleIcon />} title={tErrors(errorCode)} />}
      <div className="flex w-full items-end gap-3 rounded-2xl border border-border bg-surface p-3">
        <textarea
          rows={1}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          aria-label={t('placeholder')}
          placeholder={t('placeholder')}
          maxLength={FLEET_ASSISTANT_TEXT_MAX_CHARS}
          className="max-h-40 min-h-[2.5rem] w-full resize-none bg-transparent text-base text-text-primary placeholder:text-text-placeholder focus-visible:outline-none"
        />
        <Button type="submit" size="md" disabled={!canSend}>
          {t('send')}
        </Button>
      </div>
    </form>
  );
}
